import { Logger } from '@eluve/logger';

import { DexieDb } from './files-db';

const META_KEY = 'deviceId';

const storageKey = (userId: string, tenantId: string) =>
  `eluve.device_id.${userId}.${tenantId}`;

const bootstrapFlagKey = (userId: string, tenantId: string) =>
  `eluve.device_id.bootstrapped.${userId}.${tenantId}`;

type ResolveDeviceIdArgs = {
  db: DexieDb;
  userId: string;
  tenantId: string;
  logger: Logger;
};

const inflight = new Map<string, Promise<string>>();

const readFromLocalStorage = (
  userId: string,
  tenantId: string,
): string | null => {
  try {
    return (
      globalThis.localStorage?.getItem(storageKey(userId, tenantId)) ?? null
    );
  } catch {
    return null;
  }
};

const writeToLocalStorage = (
  userId: string,
  tenantId: string,
  value: string,
): void => {
  try {
    globalThis.localStorage?.setItem(storageKey(userId, tenantId), value);
  } catch {
    // localStorage may be disabled (private mode, quota); the Dexie mirror is
    // still authoritative on the next boot.
  }
};

const readFromDexie = async (db: DexieDb): Promise<string | null> => {
  const row = await db.meta.get(META_KEY);
  return row?.value ?? null;
};

const writeToDexie = async (db: DexieDb, value: string): Promise<void> => {
  await db.meta.put({ key: META_KEY, value });
};

const markBootstrapped = (userId: string, tenantId: string): void => {
  try {
    globalThis.localStorage?.setItem(bootstrapFlagKey(userId, tenantId), '1');
  } catch {
    // No-op — bootstrap flag is best-effort observability.
  }
};

const wasBootstrappedBefore = (userId: string, tenantId: string): boolean => {
  try {
    return (
      globalThis.localStorage?.getItem(bootstrapFlagKey(userId, tenantId)) ===
      '1'
    );
  } catch {
    return false;
  }
};

const mintNewDeviceId = (): string => {
  if (
    typeof crypto === 'undefined' ||
    typeof crypto.randomUUID !== 'function'
  ) {
    throw new Error(
      'crypto.randomUUID is not available; cannot mint device ID',
    );
  }
  return crypto.randomUUID();
};

// The API contract requires deviceId to parse as `z.string().uuid()`. Mirror
// that validator exactly so a UUID-shaped value with an invalid version or
// variant nibble (e.g. all-zero) can't pass here and then 400 every prepare
// / orphan-list request.
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isValidDeviceId = (value: string | null): value is string =>
  value !== null && UUID_PATTERN.test(value);

const doResolve = async ({
  db,
  userId,
  tenantId,
  logger,
}: ResolveDeviceIdArgs): Promise<string> => {
  const [rawLocal, rawDexie] = await Promise.all([
    Promise.resolve(readFromLocalStorage(userId, tenantId)),
    readFromDexie(db),
  ]);

  // Reject anything that wouldn't pass the API contract's UUID validation;
  // a corrupted persisted value must not be reused.
  const localValue = isValidDeviceId(rawLocal) ? rawLocal : null;
  const dexieValue = isValidDeviceId(rawDexie) ? rawDexie : null;
  if (rawLocal !== null && localValue === null) {
    logger.warn('Discarding non-UUID device_id in localStorage', {
      userId,
      tenantId,
    });
  }
  if (rawDexie !== null && dexieValue === null) {
    logger.warn('Discarding non-UUID device_id in Dexie', { userId, tenantId });
  }

  if (localValue && dexieValue) {
    if (localValue === dexieValue) {
      return localValue;
    }
    // Divergence — trust localStorage (more durable across extension lifecycle)
    // and heal Dexie. Either source is acceptable; pick one deterministically.
    logger.warn(
      'device_id divergence between localStorage and Dexie; healing Dexie',
      {
        userId,
        tenantId,
      },
    );
    await writeToDexie(db, localValue);
    return localValue;
  }

  if (localValue && !dexieValue) {
    await writeToDexie(db, localValue);
    return localValue;
  }

  if (!localValue && dexieValue) {
    writeToLocalStorage(userId, tenantId, dexieValue);
    return dexieValue;
  }

  const newId = mintNewDeviceId();
  const cause: 'first_boot' | 'both_cleared' = wasBootstrappedBefore(
    userId,
    tenantId,
  )
    ? 'both_cleared'
    : 'first_boot';

  writeToLocalStorage(userId, tenantId, newId);
  await writeToDexie(db, newId);
  markBootstrapped(userId, tenantId);

  logger.info('device_id_minted', {
    userId,
    tenantId,
    cause,
  });

  return newId;
};

/**
 * Resolve the per-(userId, tenantId) device ID. Lookup order: localStorage →
 * Dexie meta row → mint via crypto.randomUUID. Heals divergence by writing the
 * authoritative value into the missing/stale store. Concurrent callers during
 * boot dedupe via an in-memory Promise cache. [ENG-4180]
 */
export const resolveDeviceId = (args: ResolveDeviceIdArgs): Promise<string> => {
  const key = `${args.userId}.${args.tenantId}`;
  const existing = inflight.get(key);
  if (existing) {
    return existing;
  }
  const promise = doResolve(args).catch((error) => {
    inflight.delete(key);
    throw error;
  });
  inflight.set(key, promise);
  return promise;
};

/**
 * Test-only hook to reset the in-memory resolver cache. Not exported from the
 * package index — only the spec file imports it directly.
 */
export const __resetDeviceIdCacheForTests = (): void => {
  inflight.clear();
};
